import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import TextField from 'components/uiLibrary/TextField/TextField';
import Button from 'components/uiLibrary/Button/Button';
import Checkbox from 'components/uiLibrary/CheckBox';
import Loader from 'components/uiLibrary/Loader';
import Typography from 'components/uiLibrary/Typography';
import Drawer from 'components/uiLibrary/Drawer';
import ParseHtml from 'components/ParseHtml';
import useTracking from 'components/Globals/Analytics';

import {
  GOOGLE_OLD_TRACKING_SERVICES,
  ACTION_CALLBACK_STATUS,
  ACTION_CALLBACK_STEPS,
  SUB_COMPONENTS,
} from 'components/Globals/Analytics/constants';
import { useLoginLink } from 'components/Globals/Accounts/Login/DeviceManagement';

import { useCreateUserMutation, useResendOtpMutation, useCreateAdditionalEmail } from 'containers/Accounts/queries';
import { errorMessageConverter } from 'containers/Accounts/utils';
import { REGISTRATION_STEPS } from 'containers/Accounts/constants';

import { TP } from 'constants/index';
import { OB_EMAIL_ID } from 'constants/cookieConstants';

import { TRACK_EVENTS, REGISTRATION_EVENT_IDS } from 'utils/tracking';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import { getUserByEmail } from 'utils/API';
import { getCookie } from 'utils/cookie';
import { useDialogs } from 'utils/hooks/useDialogs';
import { useTranslation, Trans } from 'src/i18n';
import { selectLanguage } from 'store/selectors';
import { useUserData } from 'utils/hooks/useAuthenticatedUser';

import classes from './ExistingEmailVerification.module.scss';

const TERMS = {
  PRIVACY_POLICY: 'PRIVACY_POLICY',
  TERMS_AND_CONDITIONS: 'TERMS_AND_CONDITIONS',
};

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email(`${TP}.FN_INVALID_EMAIL`)
    .required(`${TP}.FN_REQUIRED`),
});

const ExistingEmailVerification = ({
  handleFormType,
  snackbarShowMessage,
  setCurrentEmail,
  allSteps = {},
  trackingData = {},
  scrollIntoView,
  setLoginViaOtp,
  currentEmail,
  setIsModalCloseDisabled,
  setShowUpdateUser,
  isAosVerification = false,
  setAdditionalEmailData,
  whiteListedOrganizations,
  isReturningUser,
  accountSharing,
  isPaywall,
}) => {
  const track = useTracking();
  const user = useUserData();
  const [formId] = useState(uuidv4());
  const prefilledEmail = isAosVerification ? '' : currentEmail || getCookie(OB_EMAIL_ID);
  const { isMobile } = useDeviceTypeLayouts();
  const { t } = useTranslation('NS_REGISTRATION_V4');
  const { isLoginDialog, setIsCustomerFormOpen, setIsLoginDialog } = useDialogs();
  const preferredLanguage = useSelector(selectLanguage());
  const [selectedTerm, setSelectedTerm] = useState(null);

  const { handleLoginLink } = useLoginLink({
    track,
    trackingData,
    isReturningUser,
    isLoginDialog,
    setCurrentEmail,
    handleFormType,
    allSteps,
    setLoginViaOtp,
    accountSharing,
  });

  const createUserMutation = useCreateUserMutation({
    onError: error => {
      const errorMessage = errorMessageConverter(error);
      track.actionCallback({
        ...trackingData,
        meta: {
          step: ACTION_CALLBACK_STEPS.CREATE_USER,
          actionStatus: ACTION_CALLBACK_STATUS.FAILURE,
          errorMessage,
        },
      });
      formik.setFieldError('email', errorMessageConverter(error));
    },
    onSuccess: response => {
      const userData = response.data;
      track.actionCallback({
        ...trackingData,
        meta: {
          step: ACTION_CALLBACK_STEPS.CREATE_USER,
          actionStatus: ACTION_CALLBACK_STATUS.SUCCESS,
        },
      });
      if (userData) {
        handleFormType(allSteps.VALIDATE);
        snackbarShowMessage(t(`${TP}.m_PASSCODE_MESSAGE_4_DIGIT`), 1000);
        track.actionCallback({
          ...trackingData,
          meta: {
            step: ACTION_CALLBACK_STEPS.NEW_REGISTRATION,
            actionStatus: ACTION_CALLBACK_STATUS.SUCCESS,
          },
        });
      }
    },
  });

  const { mutate: sendOtpMutation, isLoading: isSendOtpLoading } = useResendOtpMutation({
    onError: error => {
      formik.setFieldError('email', errorMessageConverter(error));
      track.actionCallback({
        ...trackingData,
        meta: {
          step: ACTION_CALLBACK_STEPS.SEND_PASSCODE_TO_EMAIL,
          actionStatus: ACTION_CALLBACK_STATUS.FAILURE,
          errorMessage: errorMessageConverter(error),
        },
      });
    },
    onSuccess: () => {
      track.actionCallback({
        ...trackingData,
        meta: {
          step: ACTION_CALLBACK_STEPS.SEND_PASSCODE_TO_EMAIL,
          actionStatus: ACTION_CALLBACK_STATUS.SUCCESS,
        },
      });
      handleFormType(allSteps.VALIDATE);
    },
  });

  const { mutate: createNewEmail } = useCreateAdditionalEmail(user?.id, {
    onError: error => {
      formik.setFieldError('email', errorMessageConverter(error));
      track.actionCallback({
        ...trackingData,
        meta: {
          step: ACTION_CALLBACK_STEPS.ADD_EMAIL,
          actionStatus: ACTION_CALLBACK_STATUS.FAILURE,
        },
      });
    },
    onSuccess: response => {
      setAdditionalEmailData(response.data);
      handleFormType(allSteps.VALIDATE);
      track.actionCallback({
        ...trackingData,
        meta: {
          step: ACTION_CALLBACK_STEPS.ADD_EMAIL,
          actionStatus: ACTION_CALLBACK_STATUS.SUCCESS,
        },
      });
    },
  });

  const validateEmail = async (values, setSubmitting) => {
    const payload = {
      email: values.email,
    };
    const response = await getUserByEmail(payload.email);
    const userData = response?.data;

    setCurrentEmail(payload.email);

    if (userData?.email) {
      const loginViaOtp = !userData?.emailVerified && userData?.passwordSetupRequired;
      if (userData.passwordSetupRequired && userData?.emailVerified) {
        handleLoginLink(userData.email);
        return;
      }
      handleFormType(loginViaOtp ? allSteps.VALIDATE : allSteps.EMAIL);

      if (loginViaOtp) {
        setLoginViaOtp(loginViaOtp);
        setShowUpdateUser(true);
        sendOtpMutation({ email: userData?.email, otpType: 'EMAIL_LOGIN', popupSource: isLoginDialog?.source });
      }
      track.actionCallback({
        ...trackingData,
        meta: {
          step: ACTION_CALLBACK_STEPS.EXISTING_USER,
          actionStatus: ACTION_CALLBACK_STATUS.SUCCESS,
        },
      });
    } else {
      createUserMutation.mutate(payload);
      setShowUpdateUser(true);
      track.click(
        {
          name: TRACK_EVENTS.CLICK,
          data: {
            id: REGISTRATION_EVENT_IDS.REGISTRATION_REGISTER_BUTTON,
            step: REGISTRATION_STEPS.userRegistration,
          },
        },
        GOOGLE_OLD_TRACKING_SERVICES,
      );
    }
    setSubmitting(false);
    scrollIntoView();
  };

  const multipleEmailHandler = async (values, setSubmitting) => {
    createNewEmail({ email: values.email });
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues: {
      email: prefilledEmail,
      firstName: '',
      lastName: '',
      preferredLanguage,
      termsAccepted: false,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const existingEmail = user?.userEmails?.find(email => email?.email === values?.email);
      if (!isAosVerification) {
        validateEmail(values, setSubmitting);
        return;
      }

      if (!existingEmail) {
        multipleEmailHandler(values, setSubmitting);
        return;
      }

      const { isLinkedToOrganization, isSalesProspectEmail } = existingEmail?.salesProspectEmail || {};
      if (isSalesProspectEmail) {
        if (isLinkedToOrganization && whiteListedOrganizations.data.length > 0) {
          handleFormType(allSteps.NEW_PROFESSIONAL_EMAIL);
          track.click({
            ...trackingData,
            component: SUB_COMPONENTS.CLAIM_ORGANIZATION,
            subComponent: SUB_COMPONENTS.CONFIRM_CTA,
          });
        } else {
          setAdditionalEmailData(existingEmail);
          handleFormType(allSteps.NEW_PROFESSIONAL_EMAIL);
          track.click({
            ...trackingData,
            component: SUB_COMPONENTS.CLAIM_ORGANIZATION,
            subComponent: SUB_COMPONENTS.CONFIRM_CTA,
          });
        }
      } else if (existingEmail?.email && isAosVerification) {
        setIsCustomerFormOpen(true);
        setIsLoginDialog({ isOpen: false });
      } else {
        multipleEmailHandler(values, setSubmitting);
      }
    },
  });
  const isSubmitDisabled = !formik.isValid || (!isAosVerification && !formik.values.termsAccepted);

  const handleTermsClick = term => {
    setSelectedTerm(term);
    setIsModalCloseDisabled(true);
  };

  return (
    <div
      className={classnames(classes.existingEmailVerification, {
        [classes.existingEmailVerification__paywall]: isPaywall,
      })}
    >
      <div className={classes.existingEmailVerification__header}>
        {!isPaywall && (
          <Typography size="18" weight="medium" className={classes.existingEmailVerification__header_title}>
            {!isAosVerification ? t(`${TP}.FN_LOGIN_REGISTER_AUTH_TITLE`) : t(`${TP}.WORK_EMAIL_VERIFICATION`)}
          </Typography>
        )}
        {isPaywall && (
          <Typography size="18" weight="medium" className={classes.existingEmailVerification__header_title}>
            {t(`${TP}.OPERABASE_CONTINUE_LOGIN`)} <br />
            {t(`${TP}.FREE_TO_USE`)}
          </Typography>
        )}
        {isAosVerification && (
          <Typography size="14" color="secondary">
            {t(`${TP}.WORK_EMAIL_VERIFICATION_VERIFICATION`)}
          </Typography>
        )}
      </div>
      <form onSubmit={formik.handleSubmit} className={classes.form}>
        <div className={classes.inputContainer}>
          <TextField
            id="email"
            name="email"
            label={
              <span>
                {t(`${TP}.m_EMAIL`)} <span className={classes.form_required}>*</span>
              </span>
            }
            variant="outlined"
            fullWidth
            {...formik.getFieldProps('email')}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && t(formik.errors.email)}
            InputLabelProps={{
              shrink: true,
            }}
            onClick={() => {
              track.click({ ...trackingData, subComponent: SUB_COMPONENTS.EMAIL_INPUT });
            }}
            className={classes.input}
          />
        </div>
        {(formik?.isSubmitting || createUserMutation?.isLoading) && (
          <div className={classes.loader}>
            <Loader />
            <Typography size="12">{t(`${TP}.FN_LOADER_TEXT_EXISTING_USER`)}</Typography>
          </div>
        )}
        {!isAosVerification && (
          <div className={classes.inputContainer__privacyCheckbox}>
            <Checkbox
              name="termsAccepted"
              id="termsAccepted"
              checked={formik.values.termsAccepted}
              onChange={e => {
                track.click({ ...trackingData, subComponent: SUB_COMPONENTS.ACCEPT_PRIVACY_CHECKBOX });
                formik.handleChange(e);
              }}
              className={classes.inputContainer__privacyCheckbox_checkbox}
            />
            <Trans
              ns="NS_REGISTRATION_V4"
              i18nKey={`${TP}.REGISTRATION_ACCEPT_PRIVACY`}
              components={{
                policy: <Button onClick={() => handleTermsClick(TERMS.PRIVACY_POLICY)} variant="text" />,
                terms: <Button onClick={() => handleTermsClick(TERMS.TERMS_AND_CONDITIONS)} variant="text" />,
              }}
            />
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          shape="rectangle"
          className={classes.button}
          disabled={isSubmitDisabled || createUserMutation?.isLoading || formik?.isSubmitting || isSendOtpLoading}
          isLoading={createUserMutation?.isLoading || formik?.isSubmitting || isSendOtpLoading}
          trackingData={{ ...trackingData, subComponent: SUB_COMPONENTS.VERIFY_EMAIL }}
        >
          {t(`${TP}.m_NEXTSTEP`)}
        </Button>
      </form>
      <Drawer
        isOpen={!!selectedTerm}
        onClose={() => setSelectedTerm(null)}
        styles={{ drawerRoot: classes.drawerRoot, drawerPaper: classes.drawerPaper, content: classes.drawerContent }}
        title={selectedTerm === TERMS.PRIVACY_POLICY ? t(`${TP}.FN_PRIVPOL`) : t(`${TP}.FN_TERMCOND`)}
        placement={isMobile ? 'bottom' : 'right'}
        allowMobileDrawer
        key={formId}
      >
        <ParseHtml>
          {selectedTerm === TERMS.PRIVACY_POLICY ? t(`${TP}.FN_PRIVACY_POLICY`) : t(`${TP}.TEXT_TERM`)}
        </ParseHtml>
      </Drawer>
    </div>
  );
};

export default ExistingEmailVerification;
